import "./sentry"; // init crash reporting before anything else
import "./lib/polyfills"; // install global runtime polyfills before any code runs
import { Fragment, StrictMode } from "react";

// StrictMode is OFF for now: its dev-only double-invoked effects trip a bug
// in @react-three/fiber 9.x — the <Canvas> unmount cleanup fires once against
// the live root at mount, arming r3f's 500ms-delayed teardown, which then
// deletes the still-mounted root from the registry its render loop iterates.
// Every GL canvas freezes on its last frame (`_roots` collapses to 0 on a
// view switch; the dev-web "arrangement doesn't respond to scroll/zoom" bug).
// Prod builds are unaffected either way (StrictMode is inert there). Flip
// back to `StrictMode` once r3f fixes teardown (v10 is in alpha).
const MaybeStrictMode = Fragment as typeof StrictMode;
import { createRoot } from "react-dom/client";
import "./index.css";
import { isMobile as checkMobile, isTauri } from "@/platform";
import { fePerf } from "@/lib/fePerf";
import { IconStrokeProvider } from "@/lib/IconStrokeProvider";
import { matchPreviewPath } from "./components/preview/route";

// Dev: log everything at debug+ and forward through the Rust terminal
// by default. See AGENTS.md → "Debugging the JS side from the Rust
// terminal".
if (import.meta.env.DEV && typeof window !== "undefined") {
  window.__SONGBIRD_LOG__ = "debug";
  window.__SONGBIRD_LOG_REMOTE__ = "debug";
}

fePerf("main.tsx top");

// Long-task observer: any main-thread block longer than 50ms shows up
// as a [fe-perf] line so post-load responsiveness regressions are
// visible in the same timeline that frames the boot phases. The
// PerformanceObserver API itself is cheap (one entry per long task,
// no per-frame cost), so this stays installed for the life of the
// session. Safari/older WebKit may not support `longtask` — wrap in
// try/catch so we don't break boot on unsupported runtimes.
try {
  if (typeof PerformanceObserver !== "undefined") {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        fePerf(
          `longtask: ${entry.duration.toFixed(0)}ms (${
            entry.name || "unknown"
          })`
        );
      }
    });
    observer.observe({ entryTypes: ["longtask"] });
  }
} catch {
  // longtask entryType not supported — skip silently.
}

let loadingRoot: ReturnType<typeof createRoot> | null = null;

// Tear down the build-time picker splash + its inline style. Called
// from any code path that needs `#root` to be visible (loading screen,
// error UI, real picker mount). Idempotent.
function removeSplash() {
  document.getElementById("splash")?.remove();
  document.getElementById("splash-style")?.remove();
}

/** Shared shell for every blocking full-screen boot notice (session
 *  held, lock recovery, WASM failure, clone failure, backend
 *  unreachable). Title + body, an optional monospace detail chip, and a
 *  button that reloads the page. Mounting stays at each call site —
 *  some paths reuse `loadingRoot`, others create a fresh root. */
function FullScreenNotice({
  title,
  body,
  detail,
  buttonLabel,
}: {
  title: string;
  body: string;
  detail?: string;
  buttonLabel: string;
}) {
  return (
    <div className={noticeWrapCls}>
      <div className={noticeColCls}>
        <div className="space-y-4">
          <h1 className={noticeTitleCls}>{title}</h1>
          <p className={noticeBodyCls}>{body}</p>
        </div>
        {detail !== undefined && <div className={noticeDetailCls}>{detail}</div>}
        <button onClick={() => location.reload()} className={noticeButtonCls}>
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

/** WASM: a live tab holds the single-session Web Lock (see
 *  data/wasm/sessionLock.ts). The caller waits on `whenSessionLockFree`
 *  and reloads when the other tab closes — this screen just says so. */
function renderSessionHeldScreen() {
  removeSplash();
  const el = document.getElementById("root")!;
  if (!loadingRoot) loadingRoot = createRoot(el);
  loadingRoot.render(
    <FullScreenNotice
      title="Tivra is active in another tab"
      body="Only one tab can hold the project files. Close the other tab and this one will take over automatically."
      buttonLabel="Reload"
    />
  );
}

function renderLoadingScreen(
  message: string,
  progress?: { current: number; total: number; fileName?: string }
) {
  // The splash assumes we'll land on the project picker; WASM/clone
  // flows that take over with a custom loading screen need to evict it
  // now so their message becomes visible.
  removeSplash();
  const el = document.getElementById("root")!;
  if (!loadingRoot) loadingRoot = createRoot(el);
  const pct =
    progress && progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : null;
  loadingRoot.render(
    <div
      className="absolute inset-0 z-100 flex items-center justify-center bg-[hsl(var(--background))]"
      data-tauri-drag-region
    >
      <div className="flex flex-col items-center space-y-6 max-w-lg w-full text-center px-8">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight text-[hsl(var(--foreground))]">
            Tivra
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] opacity-70">
            {message}
          </p>
        </div>
        {pct !== null ? (
          <div className="w-64 flex flex-col items-center space-y-2">
            <div className="w-full h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
              <div
                className="h-full rounded-full bg-[hsl(var(--foreground))] transition-all duration-200"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] opacity-60 truncate max-w-full">
              {progress!.current}/{progress!.total} samples
              {progress!.fileName ? ` — ${progress!.fileName}` : ""}
            </p>
          </div>
        ) : (
          <div className="w-8 h-8 border-2 border-[hsl(var(--muted))] border-t-[hsl(var(--foreground))] rounded-full animate-spin" />
        )}
      </div>
    </div>
  );
}

async function bootstrap() {
  // --- Stale-deploy chunk recovery (web) ---
  // A tab whose HTML predates the latest deploy lazy-imports hashed
  // chunks that no longer exist; Pages serves index.html for them and
  // the import dies with "'text/html' is not a valid JavaScript MIME
  // type". Vite surfaces every failed dynamic import as
  // `vite:preloadError` — reload once to pick up the current build.
  // The `chunkreload` marker (URL, not storage — Hard Rule #7) makes
  // the reload self-limiting: a second failure surfaces normally.
  window.addEventListener("vite:preloadError", (event) => {
    const url = new URL(location.href);
    if (url.searchParams.has("chunkreload")) return;
    event.preventDefault();
    url.searchParams.set("chunkreload", "1");
    location.replace(url.toString());
  });

  // --- Anonymous read-only preview page (/p/{id}, TIV-164) ---
  // Must win over BOTH the wasm-manifest probe and the mobile gate:
  // previews are mobile-friendly and never boot an engine, so nothing
  // below this branch should run. `matchPreviewPath` is a static import
  // (tiny, pure); `PreviewPage` is dynamic so the sync/engine module
  // graph stays out of the initial chunk.
  const previewId = matchPreviewPath(location.pathname);
  if (previewId) {
    removeSplash();
    const { PreviewPage } = await import("./components/preview");
    createRoot(document.getElementById("root")!).render(
      <MaybeStrictMode>
        <IconStrokeProvider>
          <PreviewPage id={previewId} />
        </IconStrokeProvider>
      </MaybeStrictMode>
    );
    return;
  }

  // --- WASM Bridge (must run BEFORE juce.js imports) ---
  // Activate if: ?wasm query param, VITE_WASM_MODE=true env var
  // (baked into production builds by release-wasm.sh), OR runtime
  // detection finds a WASM manifest on an HTTPS-served site.
  if (!window.__SONGBIRD__) {
    const urlParams = new URLSearchParams(location.search);
    let isWasmMode =
      urlParams.has("wasm") || import.meta.env.VITE_WASM_MODE === "true";

    // Auto-detect WASM on HTTPS sites (e.g. daw.audio on Cloudflare
    // Pages) when the build-time env var wasn't set. Probe for the
    // manifest only when there's no explicit ?ws= param (which signals
    // a headless/Cloud Run deployment that needs WebSocket mode).
    if (!isWasmMode && location.protocol === "https:" && !urlParams.has("ws")) {
      try {
        const probe = await fetch("/wasm-manifest.json", { method: "HEAD" });
        if (probe.ok && probe.headers.get("content-type")?.includes("json")) {
          console.log("[main] WASM manifest found via auto-detection");
          isWasmMode = true;
        }
      } catch {
        /* no manifest — fall through to WS mode */
      }
    }

    if (isWasmMode) {
      // Mobile-web visitors get the desktop-only notice BEFORE the multi-MB
      // WASM engine downloads — showing it after wasted the whole download on
      // a screen we don't support yet (TIV-184). The `?mode=mobile` override
      // keeps the mobile UI testable on web (TIV-177). `checkMobile()` also
      // returns true for `?mode=mobile`, so exclude it explicitly. `urlParams`
      // is the URLSearchParams already parsed at the top of this block.
      if (checkMobile() && urlParams.get("mode") !== "mobile") {
        removeSplash();
        const { DesktopOnlyNotice } = await import(
          "./components/mobile/DesktopOnlyNotice.tsx"
        );
        createRoot(document.getElementById("root")!).render(
          <MaybeStrictMode>
            <IconStrokeProvider>
              <DesktopOnlyNotice />
            </IconStrokeProvider>
          </MaybeStrictMode>
        );
        return;
      }
      // Set the flag BEFORE any dynamic imports so getPlatform() caches
      // 'wasm' instead of 'headless' when modules in the import chain
      // call isWasm() during evaluation.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__SONGBIRD_WASM_MODE__ = true;
      // Dev: mirror the browser console into the Vite terminal (there's no
      // terminal-side Rust process on WASM to forward through). Installed
      // before the bridge imports so engine-boot logs are captured too.
      if (import.meta.env.DEV) {
        const { installDevTerminalLog } = await import("./lib/devTerminalLog");
        installDevTerminalLog();
      }
      // Single-active-session guard (TIV-188's goal, reworked in TIV-213):
      // a Web Lock the browser releases on ANY holder death, so a live
      // second tab is caught here — before the engine boots — and a
      // crashed session can never masquerade as one. Holding it is also
      // what licenses the storage layer's zombie-lock self-heal.
      const { acquireSessionLock, whenSessionLockFree } = await import(
        "./data/wasm/sessionLock"
      );
      const sessionLock = await acquireSessionLock();
      if (sessionLock === "held-elsewhere") {
        console.log(
          "[main] session lock held by another tab — waiting for it to free"
        );
        renderSessionHeldScreen();
        whenSessionLockFree().then(() => {
          console.log("[main] session lock freed — reloading into the session");
          location.reload();
        });
        return;
      }
      try {
        renderLoadingScreen('Loading WASM engine...');
        // Dev/preview: honor a pending "clear all local data" request BEFORE
        // the engine worker boots and opens OPFS handles — a held handle would
        // block the wipe, so it must run here (no-op unless the picker button
        // set the flag). See devClearWasmStorage.ts.
        const { maybeClearWasmStorageOnBoot } = await import('./data/devClearWasmStorage');
        await maybeClearWasmStorageOnBoot();
        // Install the stable backend shell BEFORE importing the bridge, so
        // any listener bound while the bridge's module graph evaluates
        // (promiseHandler reply handler, store subscriptions) attaches to the
        // registry connectWasmBridge will adopt. See wasmBackendShell.ts.
        const { installWasmBackendShell } = await import(
          "./data/wasm/wasmBackendShell"
        );
        installWasmBackendShell();
        const { connectWasmBridge } = await import("./data/wasm/wasmBridge");
        await connectWasmBridge({
          healZombieLocks: sessionLock === "acquired",
        });
        console.log("[main] WASM bridge connected");
      } catch (e) {
        console.error("[main] WASM bridge failed:", e);
        const bootError = String((e as Error).message);
        // Log the extracted message explicitly — the console serializer
        // can reduce an Error to a bare stack frame, hiding the message
        // that decides which error screen we show.
        console.log(`[main] boot error message: "${bootError}"`);
        // Residual OPFS lock conflict. A live second tab is normally
        // caught by the session Web Lock above, so reaching this screen
        // means the zombie-lock heal was unavailable or failed — most
        // likely a crashed session's leftover locks. Offer the takeover
        // broadcast anyway (covers a tab race while the Web Lock API is
        // unavailable) and be honest about the restart fallback.
        if (/file\(s\) locked/.test(bootError)) {
          console.log(
            "[main] files still locked after boot — rendering recovery screen"
          );
          loadingRoot!.render(
            <FullScreenNotice
              title="Tivra can't open your project files"
              body="Another tab may be open, or a previous session crashed while holding the files. Close any other Tivra tabs and try again — if this keeps happening, restart your browser."
              buttonLabel="Try Again"
            />
          );
          return;
        }
        console.log(
          "[main] not a lock error — rendering generic failure screen"
        );
        loadingRoot!.render(
          <FullScreenNotice
            title="WASM Engine Failed"
            body="Could not load the WebAssembly audio engine. Make sure you ran build-wasm.sh first."
            detail={bootError}
            buttonLabel="Try Again"
          />
        );
        return;
      }
    }
  }

  // --- WebSocket Bridge (must run BEFORE juce.js imports) ---
  // Connect if: no native Tauri host AND a WS URL is provided via query param, env,
  // or the page is served over HTTPS (reverse-proxied headless mode).
  if (!window.__SONGBIRD__) {
    const wsUrl =
      new URLSearchParams(location.search).get("ws") ||
      (import.meta.env.VITE_WS_URL as string | undefined);
    // Auto-detect WS mode when served over HTTPS (e.g. songbird.toshbox.dev)
    const isHttps = location.protocol === "https:";
    if (wsUrl || isHttps) {
      const { connectWsBridge } = await import("./data/wsBridge");
      // When served over HTTPS, use the Vite proxy path /ws (wss://)
      // to avoid mixed-content blocking.
      let fullUrl: string;
      if (isHttps) {
        // Pass API key on the WebSocket URL so nginx can authenticate the upgrade
        const apiKey =
          new URLSearchParams(location.search).get("api_key") || "";
        const wsQuery = apiKey ? `?api_key=${apiKey}` : "";
        fullUrl = `wss://${location.host}/ws${wsQuery}`;
      } else {
        fullUrl = wsUrl!.startsWith("ws") ? wsUrl! : `ws://${wsUrl}`;
      }
      // Set global flag so isWsMode() works even without ?ws= query param
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__SONGBIRD_WS_MODE__ = true;

      // --- Clone repo if ?repo= is present (before WS connect) ---
      const urlParams = new URLSearchParams(location.search);
      const repoUrl = urlParams.get("repo");
      if (repoUrl) {
        try {
          renderLoadingScreen("Downloading project...");
          const apiKey = urlParams.get("api_key") || "";
          const cloneEndpoint = isHttps
            ? `${location.origin}/api/clone${
                apiKey ? `?api_key=${apiKey}` : ""
              }`
            : `/api/clone`;

          const cloneRes = await fetch(cloneEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url: repoUrl }),
          });
          if (!cloneRes.ok) {
            const text = await cloneRes.text();
            let msg = "Clone failed";
            try {
              msg = JSON.parse(text).error || msg;
            } catch {
              /* ignore parse errors */
            }
            throw new Error(msg);
          }

          // Read NDJSON progress stream
          const reader = cloneRes.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let clonePath = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop()!; // keep incomplete line in buffer
            for (const line of lines) {
              if (!line.trim()) continue;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              let evt: any;
              try {
                evt = JSON.parse(line);
              } catch {
                console.warn("[main] Failed to parse clone event:", line);
                continue;
              }
              console.log("[main] clone progress:", evt);
              if (evt.type === "status") {
                renderLoadingScreen(evt.message);
              } else if (evt.type === "progress") {
                renderLoadingScreen(`Downloading samples...`, {
                  current: evt.current,
                  total: evt.total,
                  fileName: evt.fileName,
                });
              } else if (evt.type === "done") {
                clonePath = evt.path;
              } else if (evt.type === "error") {
                throw new Error(evt.error);
              }
            }
          }

          if (!clonePath)
            throw new Error("No project path returned from clone service");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).__SONGBIRD_CLONE_PATH__ = clonePath;
        } catch (e) {
          console.error("[main] Clone failed:", e);
          createRoot(document.getElementById("root")!).render(
            <FullScreenNotice
              title="Clone Failed"
              body="Could not clone the project repository."
              detail={String((e as Error).message)}
              buttonLabel="Try Again"
            />
          );
          return;
        }
      }

      try {
        if (repoUrl) renderLoadingScreen("Connecting to DAW engine...");
        await connectWsBridge(fullUrl);
        // The sync engine initializes as a module side-effect (wiring.ts)
        // before connectWsBridge installs window.__SONGBIRD__, so the
        // WebSocketTransport's backend listeners were deferred. Replay them now.
        const { getSyncEngine } = await import("./sync/wiring");
        const eng = getSyncEngine();
        if (eng) {
          const wst = eng.getTransport("websocket");
          if (wst && "wireBackendListeners" in wst) {
            (
              wst as import("./sync/transports/websocket").WebSocketTransport
            ).wireBackendListeners();
          }
        }
      } catch (e) {
        console.error("[main] WS bridge connection failed:", e);
        // Pure-WS mode (no ?repo=) skipped renderLoadingScreen, so the
        // splash still covers #root. Evict it before the error UI mounts.
        removeSplash();
        createRoot(document.getElementById("root")!).render(
          <FullScreenNotice
            title="Backend Unreachable"
            body="Could not connect to the Tivra Native Server headless instance."
            detail={fullUrl}
            buttonLabel="Try Again"
          />
        );
        return; // Stop initialization
      }
    }
  }

  // Resolve which root component this window needs before touching any
  // imports. Mobile and plugin-popout modules pull in their own panel
  // trees; loading all three on every boot meant the desktop main window
  // paid for two unused module graphs.
  const urlParams = new URLSearchParams(window.location.search);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popout = (window as any).__SONGBIRD_POPOUT__ as
    | {
        mode?: string;
        pluginType?: string;
        trackId?: string;
        pluginId?: string;
      }
    | undefined;
  const mode = popout?.mode ?? urlParams.get("mode");
  const isMobile = mode === "mobile" || checkMobile();

  // Kick off theme + settings hydration in the background. Neither is
  // load-bearing for first paint:
  //   - The synchronous `applyThemeEarly` block in index.html already
  //     toggled `html.dark` / `html.light` from the localStorage mirror,
  //     so first paint is correct. `initTheme()` just reconciles with
  //     the Rust-side preference, which in practice matches the mirror.
  //   - `initSettings()` populates the Zustand store with user prefs
  //     (middleCOctave, vibeMode, etc.) that have sensible defaults.
  //     Components subscribed to the store re-render when each setting
  //     arrives.
  // Awaiting these used to be the single biggest chunk of cold-boot
  // latency (10+ serial IPC roundtrips in `initSettings`).
  fePerf("background init: theme + settings kicked off");
  void (async () => {
    const [{ initTheme }, { useGlobalSettings }, { hydrateUIFixerStore }] =
      await Promise.all([
        import("./lib/theme"),
        import("./data/settingsStore"),
        import("./data/uiFixerStore"),
      ]);
    void initTheme();
    void useGlobalSettings.getState().initSettings();
    void hydrateUIFixerStore();
  })();

  // Import only the root component this window actually needs. `mode`
  // is set by `popout.rs`'s initialization_script for Tauri popouts;
  // `checkMobile()` handles the mobile webview path.
  let rootElement: React.ReactElement;
  if (mode === "plugin") {
    const { StandalonePluginPanel } = await import(
      "./components/plugins/StandalonePluginPanel.tsx"
    );
    rootElement = <StandalonePluginPanel />;
  } else if (isMobile) {
    const { MobileApp } = await import("./components/mobile/MobileApp.tsx");
    rootElement = <MobileApp />;
  } else {
    // Seed the whole-app zoom from the viewport before first paint so the UI
    // doesn't render too-small-then-jump on a cold boot — WEB only. Native
    // zoom is WKWebView page zoom now: the Rust side applies the saved
    // `uiZoom` preference before the splash paints (ui_zoom.rs), and
    // `initSettings` → `hydrateAppZoom` sends the device seed on a first run
    // once the sync transport is live (applyAppZoom is an IPC call on native,
    // so it can't run this early). Web routes through bootAppZoomWeb, which
    // zooms documentElement on Safari (WebKit reflows vw/vh) and
    // counter-sizes #root on Blink/Gecko (which otherwise overflow the
    // viewport).
    const { appZoomEnabled, bootAppZoomWeb, seedAppZoomDefault } = await import(
      "./lib/appZoom"
    );
    if (!appZoomEnabled()) bootAppZoomWeb(seedAppZoomDefault());
    const { default: App } = await import("./App.tsx");
    rootElement = <App />;
    // Hold the (still-visible) splash spinner until the picker's display
    // faces are decoded, so its first paint is already-fonted instead of
    // flashing the system-ui fallback and swapping a frame later. Timeout-
    // raced so a slow font fetch can't pin the spinner. Picker path only —
    // the splash overlay lives in `index.html` (main window).
    const { awaitPickerFonts } = await import("./lib/theme/fonts");
    await awaitPickerFonts();
    fePerf("picker fonts ready");
  }
  fePerf("root component module loaded");

  // --- ?open=owner/repo deep link (web/WASM, TIV-164) ---
  // daw.club gallery cards land here. Awaited BEFORE the first render:
  // scheduleDeepLinkOpen arms the clone-in-flight marker synchronously,
  // so App's very first frame is the loading overlay — a restored
  // previous project never gets a visible flash before it. The actual
  // clone sends run on timers once the engine worker is up; fail-quiet
  // for malformed values.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__SONGBIRD_WASM_MODE__ === true && !mode) {
    const openRepo = urlParams.get("open");
    if (openRepo) {
      const { scheduleDeepLinkOpen } = await import("./data/deepLinkOpen");
      scheduleDeepLinkOpen(openRepo);
    }
  }

  // If a loading screen was shown (clone flow), unmount it first
  const rootEl = document.getElementById("root")!;
  const appRoot = loadingRoot || createRoot(rootEl);
  loadingRoot = null;

  // Remove the build-time splash (injected by `pickerSplashPlugin` in
  // vite.config.ts) in the same synchronous task as `appRoot.render` —
  // both DOM ops complete before the browser's next paint, so the user
  // sees rootElement (picker, autoload LoadingScreen, etc.) on the
  // first frame with no splash-on-top gap. Earlier code deferred this
  // to two RAFs after render to be defensive about WebKit paint
  // ordering, but that left the splash sitting over progress events
  // that fired between render and the second RAF.
  removeSplash();
  appRoot.render(
    <MaybeStrictMode>
      <IconStrokeProvider>{rootElement}</IconStrokeProvider>
    </MaybeStrictMode>
  );
  fePerf("React render called");

  // WebView blackout recovery: heartbeat + resume repaint for the desktop
  // main window. Pairs with the native watchdog (webview_watchdog.rs) that
  // reloads the WKWebView if it goes dark after a sleep / battery-drain
  // wake. Main window only — a popout's heartbeat would mask a dead main
  // window. Skipped in browser (WS/WASM) modes, which have no native host.
  if (isTauri() && mode !== "plugin" && !isMobile) {
    void import("./lib/recovery/webviewRecovery").then((m) =>
      m.installWebviewRecovery()
    );
  }

  // --- Auto-open cloned project ---
  // If we cloned a repo via ?repo=, open it now that the bridge is connected.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clonePath = (window as any).__SONGBIRD_CLONE_PATH__;
  if (clonePath) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (window as any).__SONGBIRD_CLONE_PATH__;
    const { send } = await import("./sync/api");
    // Small delay to let the headless engine finish its init handshake
    setTimeout(() => {
      console.log("[main] Auto-opening cloned project:", clonePath);
      send("project.choice", { choice: `openRecent:${clonePath}` });
    }, 500);
  }
}

bootstrap().catch(console.error);

// ─── Class string constants (FullScreenNotice) ───────────────────────
const noticeWrapCls =
  "absolute inset-0 z-100 flex items-center justify-center bg-[hsl(var(--background))]";
const noticeColCls =
  "flex flex-col items-center space-y-10 max-w-lg w-full text-center px-8";
const noticeTitleCls =
  "text-4xl font-bold tracking-tight text-[hsl(var(--foreground))]";
const noticeBodyCls =
  "text-sm text-[hsl(var(--muted-foreground))] opacity-70 max-w-sm mx-auto";
const noticeDetailCls =
  "type-readout-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-3 py-1.5 rounded-md truncate max-w-xs break-all";
const noticeButtonCls =
  "mt-2 px-4 py-2 text-sm rounded-md bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-90 transition-opacity";
